import { CartApiError, type CartApiErrorType } from './cartApi.types';
import type { RawCartData } from '../types/cartTypes';

// 定义请求体结构 (根据 Shopify Ajax API 要求)
/**
 * 向购物车添加商品时的请求负载
 */
interface AddToCartPayload {
  items: {
    /** 变体 ID */
    id: number;
    /** 数量 */
    quantity: number;
    /** 自定义行属性 */
    properties?: Record<string, string>;
    /** 订阅计划 ID */
    selling_plan?: number;
  }[];
}

const DEFAULT_TIMEOUT = 5000;
const RETRY_TIMEOUT = 3000;
const REQUEST_SOURCE = '_source=react';
const JSON_HEADERS = { 'Content-Type': 'application/json', 'X-Request-Source': 'react' };

function getShopifyRoot(): string {
  if (typeof window === 'undefined') return '/';
  const root = (window as any).Shopify?.routes?.root ?? '/';
  return root.endsWith('/') ? root : `${root}/`;
}

function supportsAbortController(): boolean {
  return typeof AbortController !== 'undefined';
}

function mergeAbortSignals(primary?: AbortSignal, secondary?: AbortSignal): AbortSignal | undefined {
  if (!primary) return secondary;
  if (!secondary) return primary;
  const anyFn = typeof AbortSignal !== 'undefined'
    ? (AbortSignal as unknown as { any?: (signals: AbortSignal[]) => AbortSignal }).any
    : undefined;
  if (typeof anyFn === 'function') {
    return anyFn([primary, secondary]);
  }
  if (!supportsAbortController()) return primary;
  const controller = new AbortController();
  const abort = () => controller.abort();
  primary.addEventListener('abort', abort, { once: true });
  secondary.addEventListener('abort', abort, { once: true });
  return controller.signal;
}
/**
 * 为 Promise 添加超时限制，超过指定时间后抛出 TIMEOUT 错误。
 * @param promise 原始请求 Promise
 * @param ms 超时时间，默认 8000ms
 * @returns 包装后的 Promise
 */
async function withTimeout<T>(
  request: (signal?: AbortSignal) => Promise<T>,
  ms = DEFAULT_TIMEOUT
): Promise<T> {
  const controller = supportsAbortController() ? new AbortController() : null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      if (controller) {
        controller.abort();
      }
      reject(new CartApiError({
        type: 'TIMEOUT',
        message: `Request timed out after ${ms}ms`
      }));
    }, ms);
  });

  try {
    return await Promise.race([request(controller?.signal), timeoutPromise]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

/**
 * 判定错误是否可重试（网络错误、超时、限流）。
 * @param err 捕获到的错误
 * @returns 是否应当尝试重试
 */
function shouldRetry(err: unknown): boolean {
  if (!(err instanceof CartApiError)) return false;
  if (err.type === 'TIMEOUT' && !supportsAbortController()) return false;
  return err.type === 'NETWORK' || err.type === 'TIMEOUT' || err.type === 'RATE_LIMIT';
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'AbortError';
}

/**
 * 带重试的请求包装器，采用指数回退。
 * @param fn 需要执行的异步函数
 * @param retries 最大重试次数，默认 2
 * @param baseDelay 基础延迟，默认 300ms
 * @returns 请求结果
 */
async function withRetry<T>(
  fn: (timeoutMs: number) => Promise<T>,
  { retries = 1, baseDelay = 300, timeoutMs = DEFAULT_TIMEOUT, retryTimeoutMs = RETRY_TIMEOUT } = {}
): Promise<T> {
  let attempt = 0;
  while (true) {
    try {
      const effectiveTimeout = attempt === 0 ? timeoutMs : retryTimeoutMs;
      return await fn(effectiveTimeout);
    } catch (err) {
      if (!shouldRetry(err) || attempt >= retries) throw err;
      const delay = baseDelay * Math.pow(2, attempt);
      await new Promise((r) => setTimeout(r, delay));
      attempt++;
    }
  }
}

/**
 * 统一处理 Shopify Ajax API 响应，自动解析错误码并抛出标准化异常。
 * @param res fetch 返回的 Response
 * @returns 解析后的 JSON 数据
 */
async function handleResponse(res: Response) {
  if (!res.ok) {
    console.error('cartApi.handleResponse non-OK', res.status, res.statusText);
    let type: CartApiErrorType = 'UNKNOWN';
    let message = `Request failed with status ${res.status}`;
    let raw: unknown;

    let bodyText = '';
    try {
      bodyText = await res.text();
    } catch { /* ignore */ }

    if (res.status === 429) {
      type = 'RATE_LIMIT';
      message = 'Too many requests';
    } else if (res.status >= 500) {
      type = 'NETWORK';
      message = 'Server error';
    } else if (res.status >= 400) {
      type = 'SHOPIFY_VALIDATION';
      try {
        if (bodyText) {
          const errorData = JSON.parse(bodyText);
          message = errorData.description || errorData.message || message;
          raw = errorData;
        }
      } catch {
        // JSON 解析失败，保持默认错误类型
      }
    }

    throw new CartApiError({
      type,
      message,
      status: res.status,
      raw,
      bodyText
    });
  }
  console.log('cartApi.handleResponse OK', res.url, res.status);
  return res.json();
}

type RequestJsonOptions = {
  method: 'GET' | 'POST';
  url: string;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  retries?: number;
  timeoutMs?: number;
  retryTimeoutMs?: number;
};

async function requestJson<T>({
  method,
  url,
  body,
  headers,
  signal,
  retries,
  timeoutMs,
  retryTimeoutMs
}: RequestJsonOptions): Promise<T> {
  console.log('[cartApi.requestJson]', { method, url, body });
  return withRetry(
    async (effectiveTimeout) => {
      try {
        return await withTimeout((timeoutSignal) => fetch(url, {
          method,
          headers: {
            ...(body ? JSON_HEADERS : {}),
            ...(headers ?? {})
          },
          signal: mergeAbortSignals(signal, timeoutSignal),
          body: body ? JSON.stringify(body) : undefined
        }).then(handleResponse), effectiveTimeout);
      } catch (error) {
        if (isAbortError(error)) throw error;
        if (error instanceof CartApiError) throw error;
        throw new CartApiError({
          type: 'NETWORK',
          message: error instanceof Error ? error.message : 'Network request failed',
          raw: error
        });
      }
    },
    { retries, timeoutMs, retryTimeoutMs }
  );
}

export type CartUpdatePayload = {
  updates?: Record<string, number>;
  attributes?: Record<string, string | null>;
  note?: string;
  discount?: string;
};

async function _postUpdate(payload: CartUpdatePayload, signal?: AbortSignal): Promise<RawCartData> {
  const root = getShopifyRoot();
  return requestJson<RawCartData>({
    method: 'POST',
    url: `${root}cart/update.js?${REQUEST_SOURCE}`,
    body: payload,
    signal
  });
}

/**
 * 纯粹的网络请求层，封装了对 Shopify Ajax API 的访问。
 * 包含重试和超时逻辑。
 */
export const cartApi = {

  /**
   * 获取购物车数据 (GET /cart.js)
   */
  getCart: async (): Promise<RawCartData> => {
    const root = getShopifyRoot();
    return requestJson<RawCartData>({
      method: 'GET',
      url: `${root}cart.js?${REQUEST_SOURCE}`
    });
  },

  /**
   * 添加商品 (POST /cart/add.js)
   */
  addToCart: async (payload: AddToCartPayload) => {
    const root = getShopifyRoot();
    console.log('cartApi.addToCart request', payload);
    return requestJson<unknown>({
      method: 'POST',
      url: `${root}cart/add.js?${REQUEST_SOURCE}`,
      body: payload,
      retries: 0
    });
  },

  /**
   * 专门用于数量更新 (POST /cart/change.js)
   */
  updateLineItem: async (id: string, quantity: number, signal?: AbortSignal) => {
    const root = getShopifyRoot();
    return requestJson<RawCartData>({
      method: 'POST',
      url: `${root}cart/change.js?${REQUEST_SOURCE}`,
      body: { id, quantity },
      signal
    });
  },

  /**
   * 专门用于属性更新 (POST /cart/update.js)
   */
  updateAttributes: async (attributes: Record<string, string | null>, signal?: AbortSignal) => {
    return _postUpdate({ attributes }, signal);
  },

  /**
   * 专门用于折扣码更新 (POST /cart/update.js)
   */
  updateDiscountCodes: async (discountPayload: string, signal?: AbortSignal) => {
    return _postUpdate({ discount: discountPayload }, signal);
  },

  /**
   * 专门用于备注更新 (POST /cart/update.js)
   */
  updateNote: async (note: string, signal?: AbortSignal) => {
    return _postUpdate({ note }, signal);
  },
};
